import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DOT_KEYS, STICKER_SIZE, type Dot, type DotKey } from "./geometry";

const labels: Record<DotKey, string> = {
  L: "红点（左眼）",
  R: "蓝点（右眼）",
  M: "绿点（嘴）",
};
const colors: Record<DotKey, string> = { L: "#f00", R: "#00f", M: "#00c800" };
type Anchors = Partial<Record<DotKey, Dot>>;

export default function StarterKitPreview({
  open,
  onOpenChange,
  image,
  anchors,
  automatic,
  onChange,
  onDownload,
  busy,
  stale,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  image: string;
  anchors: Anchors;
  automatic: Anchors;
  onChange: (anchors: Anchors) => void;
  onDownload: () => void;
  busy: boolean;
  stale: boolean;
}) {
  const [selected, setSelected] = useState<DotKey>("L");
  const missing = DOT_KEYS.filter((key) => !anchors[key]);
  const setPoint = (key: DotKey, x: number, y: number) =>
    onChange({
      ...anchors,
      [key]: {
        x: Math.max(8, Math.min(STICKER_SIZE - 8, x)),
        y: Math.max(8, Math.min(STICKER_SIZE - 8, y)),
      },
    });
  const fromPointer = (
    event: React.PointerEvent<SVGSVGElement>,
    key: DotKey,
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setPoint(
      key,
      ((event.clientX - rect.left) * STICKER_SIZE) / rect.width,
      ((event.clientY - rect.top) * STICKER_SIZE) / rect.height,
    );
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>确认画师起始包点位</DialogTitle>
          <DialogDescription>
            拖动色点，或选择色点后点击图片定位；方向键可微调 1 像素。
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          {DOT_KEYS.map((key) => (
            <Button
              key={key}
              type="button"
              size="sm"
              variant={selected === key ? "default" : "outline"}
              onClick={() => setSelected(key)}
            >
              {labels[key]}
              {!anchors[key] ? "（待设置）" : ""}
            </Button>
          ))}
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={stale || busy}
            onClick={() => onChange({ ...automatic })}
          >
            重置自动位置
          </Button>
        </div>
        <svg
          viewBox={`0 0 ${STICKER_SIZE} ${STICKER_SIZE}`}
          className="mx-auto w-full max-w-[512px] touch-none rounded border bg-[repeating-conic-gradient(#eee_0_25%,#fff_0_50%)] bg-[length:12px_12px]"
          role="application"
          aria-label="五官点位预览"
          tabIndex={0}
          onKeyDown={(event) => {
            if (stale || busy) return;
            const delta: Record<string, [number, number]> = {
              ArrowLeft: [-1, 0],
              ArrowRight: [1, 0],
              ArrowUp: [0, -1],
              ArrowDown: [0, 1],
            };
            const point = anchors[selected];
            if (point && delta[event.key]) {
              event.preventDefault();
              const [dx, dy] = delta[event.key];
              setPoint(selected, point.x + dx, point.y + dy);
            }
          }}
          onPointerDown={(event) => {
            if (stale || busy) return;
            const key = (event.target as SVGElement).dataset.key as
              DotKey | undefined;
            const target = key && DOT_KEYS.includes(key) ? key : selected;
            setSelected(target);
            event.currentTarget.setPointerCapture(event.pointerId);
            event.currentTarget.dataset.dragKey = target;
            fromPointer(event, target);
          }}
          onPointerMove={(event) => {
            const key = event.currentTarget.dataset.dragKey as
              DotKey | undefined;
            if (
              !stale &&
              !busy &&
              key &&
              event.currentTarget.hasPointerCapture(event.pointerId)
            )
              fromPointer(event, key);
          }}
          onPointerUp={(event) => {
            delete event.currentTarget.dataset.dragKey;
          }}
          onPointerCancel={(event) => {
            delete event.currentTarget.dataset.dragKey;
          }}
        >
          <image href={image} width={STICKER_SIZE} height={STICKER_SIZE} />
          {DOT_KEYS.map(
            (key) =>
              anchors[key] && (
                <circle
                  key={key}
                  data-key={key}
                  cx={anchors[key]!.x}
                  cy={anchors[key]!.y}
                  r={10}
                  fill={colors[key]}
                  stroke="white"
                  strokeWidth={4}
                />
              ),
          )}
        </svg>
        {stale ? (
          <p className="text-sm text-muted-foreground">
            五官正在更新，请稍候。
          </p>
        ) : (
          missing.length > 0 && (
            <p className="text-sm text-muted-foreground">
              未自动找到{missing.map((key) => labels[key]).join("、")}
              ，请选择对应色点并点击图片补充。
            </p>
          )
        )}
        <DialogFooter>
          <Button
            onClick={onDownload}
            disabled={busy || stale || missing.length > 0}
          >
            {busy ? "打包中…" : "确认下载"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
